import { jsx } from "react/jsx-runtime";
import { createContext, useMemo, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format, parseISO, isWithinInterval } from "date-fns";
import { DATE_FORMAT } from "../appointment-schedule.constants.js";
import { ServerSentResourceType } from "../appointment-schedule.types.js";
import { flow } from "../helpers/common.js";
import { getViewTimePeriod } from "../helpers/getViewTimePeriod.js";
import { groupOverlappingAppointments, groupByEmployee, groupBy } from "../helpers/groupBy.js";
import { useAppointments } from "../hooks/useAppointments.js";
import { useServerSentEvent } from "../hooks/useServerSentEvent.js";
import { useWorkingTimesProvider } from "./working-times-provider.js";
import "date-fns/locale";
import "fast-memoize";
import "../../../../utils/time/time.js";
import "lodash";
import "./axios-provider.js";
import "axios";
import "../helpers/getEmptyCalendarSlots.js";
import "../helpers/fillEmptyDays.js";
import "../hooks/useWorkingTimes.js";
const AppointmentsContext = createContext({ queryKey: null, data: {}, queryResult: {} });
const canUpdateCalendarView = (start, end, clinicReferenceId, appointment) => {
  if (!appointment || clinicReferenceId !== appointment.clinicReferenceId)
    return false;
  const parsedStartTime = parseISO(appointment.startTime);
  const shouldUpdateCalendarView = isWithinInterval(parsedStartTime, {
    start: parseISO(start || ""),
    end: parseISO(end || "")
  });
  return shouldUpdateCalendarView;
};
const filterByCancelledAppointments = (showCancelled, appointments) => appointments.filter(({ cancellationType }) => showCancelled || !cancellationType);
const AppointmentsProvider = ({
  children,
  auth,
  clinicReferenceId = "",
  view = "week",
  employees = [],
  date = /* @__PURE__ */ new Date(),
  showCancelled = false
}) => {
  const client = useQueryClient();
  const { nonWorkingTimes } = useWorkingTimesProvider();
  const { start, end } = useMemo(() => getViewTimePeriod(view, date), [view, date]);
  const params = useMemo(
    () => ({
      employeeReferenceIds: employees.map(({ referenceId }) => referenceId),
      startAfter: start,
      startBefore: end,
      showCancelled
    }),
    [date, employees, auth, clinicReferenceId, view, showCancelled]
  );
  const { data = [], queryKey, queryResult } = useAppointments({ clinicReferenceId, auth, params });
  const filteredData = useMemo(() => filterByCancelledAppointments(showCancelled, data), [showCancelled, data]);
  const groupedData = useMemo(
    () => flow(
      groupBy((appointment) => format(parseISO(appointment.startTime), DATE_FORMAT))(filteredData),
      groupByEmployee,
      groupOverlappingAppointments(nonWorkingTimes)
    ),
    [filteredData, nonWorkingTimes]
  );
  const handleCreateAppointment = (appointment) => {
    if (!canUpdateCalendarView(start, end, clinicReferenceId, appointment))
      return;
    queryResult.refetch();
  };
  const handleUpdateAppointment = (appointment) => {
    client.setQueryData(queryKey, (previousData) => {
      if (!previousData)
        return [];
      return previousData.map(
        (previousAppointment) => previousAppointment.referenceId === appointment.referenceId ? appointment : previousAppointment
      );
    });
  };
  const handleDeleteAppointment = (appointment) => {
    if (!canUpdateCalendarView(start, end, clinicReferenceId, appointment))
      return;
    client.setQueryData(queryKey, (previousData) => {
      if (!previousData)
        return [];
      return previousData.filter((previousAppointment) => previousAppointment.referenceId !== appointment.referenceId);
    });
  };
  useServerSentEvent(ServerSentResourceType.APPOINTMENT, "CREATED", handleCreateAppointment);
  useServerSentEvent(ServerSentResourceType.APPOINTMENT, "UPDATED", handleUpdateAppointment);
  useServerSentEvent(ServerSentResourceType.APPOINTMENT, "DELETED", handleDeleteAppointment);
  const value = useMemo(
    () => ({
      data: groupedData,
      queryKey,
      queryResult
    }),
    [queryKey, data, queryResult, groupedData]
  );
  return /* @__PURE__ */ jsx(AppointmentsContext.Provider, { value, children });
};
const useAppointmentsProvider = () => {
  const context = useContext(AppointmentsContext);
  if (context === void 0) {
    throw new Error("useAppointmentsProvider must be used within a AppointmentsProvider");
  }
  return context;
};
export {
  AppointmentsProvider,
  useAppointmentsProvider
};
